import React, { useEffect, useState, useCallback } from 'react';
import { crmService, Client } from '../../services/crmService';
import { emailService, CommunicationSummary } from '../../services/emailService';

interface Props {
  client: Client;
  refreshKey?: number;
  onContactUpdated: (c: Client) => void;
}

const replyLabel: Record<string, string> = {
  positive: 'Positive',
  neutral: 'Neutral',
  negative: 'Negative',
  opt_out: 'Opt-out',
};

const ContactOutreachStatus: React.FC<Props> = ({ client, refreshKey = 0, onContactUpdated }) => {
  const [summary, setSummary] = useState<CommunicationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const s = await emailService.getCommunicationSummary({ client_id: client.id });
      setSummary(s);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load outreach status');
    } finally {
      setLoading(false);
    }
  }, [client.id]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const toggleDnc = async (next: boolean) => {
    try {
      setBusy(true);
      const updated = await crmService.updateClient(client.id, { do_not_contact: next });
      onContactUpdated(updated);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-surface-muted/20 p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="text-sm font-semibold text-fg">Email activity</h4>
        {client.do_not_contact ? (
          <span className="text-xs font-semibold text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-950/50 px-2 py-0.5 rounded-md">
            Do Not Contact
          </span>
        ) : (
          <span className="text-xs text-fg-muted">Eligible for email</span>
        )}
      </div>

      {client.unsubscribed_at ? (
        <p className="text-xs text-fg-muted">
          Unsubscribed: {new Date(client.unsubscribed_at).toLocaleString()}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {client.do_not_contact ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void toggleDnc(false)}
            className="btn-secondary text-xs py-1.5 px-3 rounded-lg"
          >
            Clear Do Not Contact
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => void toggleDnc(true)}
            className="btn-secondary text-xs py-1.5 px-3 rounded-lg border-amber-500/40 text-amber-900 dark:text-amber-100"
          >
            Mark Do Not Contact
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-fg-muted">Loading activity…</p>
      ) : error ? (
        <p className="text-xs text-danger-fg">{error}</p>
      ) : summary ? (
        <div className="space-y-2 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="rounded-lg border border-border bg-app/60 p-2">
              <p className="font-medium text-fg-secondary">Last sent</p>
              {summary.last_outbound ? (
                <>
                  <p className="text-fg truncate">{summary.last_outbound.subject}</p>
                  <p className="text-fg-muted">
                    {summary.last_outbound.sent_at
                      ? new Date(summary.last_outbound.sent_at).toLocaleString()
                      : '—'}
                    {summary.last_outbound.template_name_snapshot || summary.last_outbound.template_name
                      ? ` · ${summary.last_outbound.template_name_snapshot || summary.last_outbound.template_name}`
                      : ''}
                  </p>
                </>
              ) : (
                <p className="text-fg-muted">—</p>
              )}
            </div>
            <div className="rounded-lg border border-border bg-app/60 p-2">
              <p className="font-medium text-fg-secondary">Last reply</p>
              {summary.last_inbound_reply ? (
                <>
                  <p className="text-fg">
                    Type:{' '}
                    <span className="font-medium">
                      {replyLabel[summary.last_inbound_reply.response_type] ??
                        summary.last_inbound_reply.response_type}
                    </span>
                  </p>
                  <p className="text-fg-muted line-clamp-2">
                    {summary.last_inbound_reply.reply_text?.replace(/\s+/g, ' ').trim()}
                  </p>
                </>
              ) : (
                <p className="text-fg-muted">No replies recorded yet</p>
              )}
            </div>
          </div>
          {summary.recent_inbound && summary.recent_inbound.length > 0 ? (
            <div>
              <p className="font-medium text-fg-secondary mb-1">Recent replies</p>
              <ul className="space-y-1 max-h-28 overflow-y-auto">
                {summary.recent_inbound.slice(0, 3).map((r) => (
                  <li key={r.id} className="text-fg-muted flex justify-between gap-2">
                    <span className="truncate">{replyLabel[r.response_type] ?? r.response_type}</span>
                    <span className="shrink-0">{new Date(r.created_at).toLocaleDateString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ContactOutreachStatus;
